import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { EnvelopeSimple, CheckCircle } from '@phosphor-icons/react';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';

const API = process.env.REACT_APP_BACKEND_URL;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

/**
 * Forgot-password landing page.
 *
 * SECURITY: this page MUST NEVER auto-transition into the
 * new-password form. The only path to that form is the email link
 * (rendered by `<ResetPasswordPage>` at `/reset-password?token=...`).
 *
 * The previous implementation read `reset_token` off the
 * `/auth/forgot-password` response and swapped views client-side —
 * that effectively let anyone reset any account just by knowing the
 * email. The new contract:
 *   1. POST email → 200 with a generic message (always, regardless of
 *      whether the email is registered).
 *   2. We render a stable confirmation card with the same wording.
 *   3. The user goes to their inbox; the link in the email is the
 *      only entry point to the new-password form.
 */
export const ForgotPasswordPage = () => {
  // Wrap the actual form in the reCAPTCHA provider ONLY when a site key
  // is present. Without a key, we render the plain form so /forgot-password
  // never breaks — protection is still enforced by SlowAPI + backend
  // reCAPTCHA (which no-ops when the secret is empty).
  if (RECAPTCHA_SITE_KEY) {
    return (
      <GoogleReCaptchaProvider
        reCaptchaKey={RECAPTCHA_SITE_KEY}
        scriptProps={{ async: true, defer: true, appendTo: 'head' }}
      >
        <ForgotPasswordInner />
      </GoogleReCaptchaProvider>
    );
  }
  return <ForgotPasswordInner />;
};

const ForgotPasswordInner = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // `useGoogleReCaptcha()` is a safe hook call — it returns `{ executeRecaptcha: undefined }`
  // when this component isn't wrapped in the provider (i.e. no site key configured).
  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      let recaptcha_token = null;
      if (executeRecaptcha) {
        try {
          recaptcha_token = await executeRecaptcha('password_reset');
        } catch (err) {
          // If reCAPTCHA fails to produce a token we still POST — the
          // backend will 429 if verification is enforced, and we present
          // the same generic confirmation card either way.
          console.warn('reCAPTCHA execute failed:', err);
        }
      }
      await axios.post(`${API}/api/auth/forgot-password`, {
        email: email.trim(),
        recaptcha_token,
      });
    } catch {
      // Swallow errors silently — surfacing them would let attackers
      // distinguish "email valid + server error" from "email missing".
    }
    // Show the confirmation card unconditionally.
    setSubmitted(true);
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-6">
        <div className="w-full max-w-md text-center">
          <div className="mb-6">
            <Link to="/" className="inline-flex flex-col items-center" data-testid="forgot-logo">
              <img src="/ato-logo.png" alt="ATÔ" className="w-[160px] md:w-[200px] h-auto object-contain" />
            </Link>
          </div>
          <div className="bg-white border border-border rounded-2xl p-8 shadow-sm" data-testid="forgot-confirmation">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-success-soft mb-4">
              <EnvelopeSimple size={26} weight="fill" className="text-success" />
            </div>
            <h2 className="text-lg md:text-xl font-medium text-text_strong tracking-wide mb-3" data-testid="forgot-confirmation-title">
              {t('auth.checkYourInbox') || 'Vérifiez votre boîte de réception'}
            </h2>
            <p className="text-text_muted mb-2" data-testid="forgot-confirmation-message">
              {t('auth.resetLinkSent') || 'Si cet email existe, un lien de réinitialisation a été envoyé.'}
            </p>
            <p className="text-xs text-text_muted mb-6">
              {t('auth.resetLinkExpiry') || 'Le lien expirera dans 60 minutes.'}
            </p>
            <Link to="/login">
              <Button variant="primary" className="w-full py-3 font-semibold" data-testid="back-to-login-after-submit">
                {t('auth.backToLogin') || 'Retour à la connexion'}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <Link to="/" className="inline-flex flex-col items-center" data-testid="forgot-logo">
            <img src="/ato-logo.png" alt="ATÔ" className="w-[160px] md:w-[200px] h-auto object-contain" />
          </Link>
        </div>
        <div className="bg-white border border-border rounded-2xl p-8 shadow-sm">
          <h2 className="text-lg md:text-xl font-medium text-text_strong tracking-wide mb-2" data-testid="forgot-title">
            {t('auth.forgotPassword')}
          </h2>
          <p className="text-text_muted mb-6">{t('auth.forgotPasswordDesc')}</p>
          <form onSubmit={handleRequestReset} className="space-y-4" data-testid="forgot-form">
            <div>
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input
                id="email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} required
                className="border border-border" data-testid="forgot-email-input"
              />
            </div>
            <Button
              type="submit" disabled={loading} variant="primary"
              className="w-full py-3 font-semibold"
              data-testid="forgot-submit-button"
            >
              {loading ? t('common.loading') : t('auth.sendResetLink')}
            </Button>
          </form>
          <p className="text-center text-sm text-text_muted mt-6">
            <Link to="/login" className="text-primary hover:underline font-medium">
              {t('auth.backToLogin')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export { CheckCircle };  // silences "unused import" if tree-shaking lags
